import { SiteUrls } from '@tableau/shared-urls';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  CompactInlineAlertWidget,
  DialogPositioningBehavior,
  DialogType,
  FilteringList,
  InlineAlertType,
  List,
  ListItem,
} from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { CompositeSessionInfoSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { AppState } from '~src/app/state/AppState';

import { Formatters,Messages } from '../../../codegen/Localize';
import { showActionableToast } from '../../../common/react/components/ActionableMessage';
import { ButtonWithSpinner } from '../../../common/react/components/ButtonWithSpinner';
import { Location } from '../../../common/react/Location';
import { SparseCachedList } from '../../../common/react/SparseCachedList';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { CollectionsErrorCode } from '../../../ts/helpers/ErrorCodes';
import { Order } from '../../../ts/helpers/Order';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { L10n } from '../../../ts/L10n/L10n';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { CreateEmptyCollectionAction, createEmptyCollectionAction } from '../../actions/spaces/ContentExplorerCreateActions';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { delayPromise } from '../../utils/Delay';
import { AddRemoveCollectionItemsData, CollectionContentItem } from './CollectionModels';
import { CollectionsDialogStyles } from './CollectionsDialogStyles';
import { ManageItemsTelemetry, sendManageItemsEvent } from './CollectionsTelemetry';
import { ICollectionInfo } from './ICollectionInfo';
import { CollectionItemsResourceQuery } from './ResourceQuery/CollectionItemsResourceQuery';
import { CollectionsResourceQuery } from './ResourceQuery/CollectionsResourceQuery';

export const ManageItemsInCollectionsDialogTestId = 'add-to-collections-dialog';
export const ManageItemsInCollectionsCreateButtonTestId = `${ManageItemsInCollectionsDialogTestId}-create`;
export const ManageItemsInCollectionsCancelButtonTestId = `${ManageItemsInCollectionsDialogTestId}-cancel`;
export const ManageItemsInCollectionsSaveButtonTestId = `${ManageItemsInCollectionsDialogTestId}-save`;
export const ManageItemsInCollectionsListContainerTestId = `${ManageItemsInCollectionsDialogTestId}-list-container`;
export const ManageItemsInCollectionsErrorTestId = `${ManageItemsInCollectionsDialogTestId}-error`;
export const ManageItemsInCollectionsSuccessToastTestId = `${ManageItemsInCollectionsDialogTestId}-success`;
export const ManageItemsInCollectionsErrorToastTestId = `${ManageItemsInCollectionsDialogTestId}-error`;
export const ManageItemsInCollectionsCollectionListItemSuffix = `collection-list-item`;

export const SEARCH_FILTER_TYPING_TIMEOUT = 300;
export const SEARCH_TERM_LIMIT = 100;
export const MAX_ITEMS_PER_COLLECTION = 1000;

export namespace ManageItemsInCollectionsDialog {
  export interface Options {
    readonly items: IContentInfo[];
    readonly targetCollectionLuids?: string[];
    readonly userId: string;
    readonly isAdmin: boolean;
    readonly siteUrls: SiteUrls;
    readonly spaceInfo: SpaceInfo;
    readonly manageItemsTelemetry: ManageItemsTelemetry;
  }
  export interface ContentsStateProps {
    readonly limitedUserVisibility: boolean;
  }

  interface ContentsDispatchProps {
    createEmptyCollection: (itemsToPossiblyAdd?: IContentInfo[], manageItemsTelemetry?: ManageItemsTelemetry) => CreateEmptyCollectionAction;
  }

  export interface ContentsProps extends Options, ContentsDispatchProps, ContentsStateProps {
    readonly closeDialog: () => void;
  }

  export interface ContentsState {
    readonly busy: boolean;
    readonly error: string;
    readonly allCollections: ICollectionInfo[];
    readonly searchResults: ICollectionInfo[];
    readonly collectionsToUpdate: Set<string>;
    readonly searchTerm: string;
    readonly useLocalFiltering: boolean;
  }

  export const CollectionsBlockSize = 1000;

  export function buildCollectionLuidToHasItemsMap(
    collectionItems: CollectionContentItem[],
    numberOfItems: number
  ): Map<string, boolean> {
    const collectionLuidToHasItemsMap = new Map<string, boolean>();

    // map collection luid to count of collectionItems
    // and if it matches numberOfItems it means
    // it contains all items were trying to add
    const counts = _.countBy(collectionItems, 'collectionLuid');

    _.each(counts, (count, collectionLuid) => {
      if (count === numberOfItems) {
        collectionLuidToHasItemsMap.set(collectionLuid, true);
      }
    });

    return collectionLuidToHasItemsMap;
  }

  const getCollectionsListItems = defaultMemoize((collectionsToDisplay: ICollectionInfo[], isNewlyCreatedFn: (collection: ICollectionInfo) => boolean, limitedUserVisibility: boolean, isAdmin: boolean, itemsToAddCount: number): ListItem[] => {
    let items: ListItem[] = [];
    if(!collectionsToDisplay) {
      items.push({
        value: 'spinner',
        disabled: true,
        content: <>
          <div className={CollectionsDialogStyles.Spinner} >
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Small}
              testId="collections-list-spinner" />
          </div>
        </>
      });
    } else if(collectionsToDisplay.length === 0) {
      items.push({ value: Messages.noMatches(), disabled: true, testId: 'no-matches-item' });
    } else {
      items = collectionsToDisplay.reduce((filtered, collection): ListItem[] => {
        // collections cache can be sparse, filter out unfetched collections
        // using `reduce` to only traverse list of collections once (could be very long)
        if(collection) {
          filtered.push({
            value: collection.luid,
            content: getCollectionListItemContent(collection, isNewlyCreatedFn(collection), limitedUserVisibility, isAdmin),
            testId: `${collection.name}-${ManageItemsInCollectionsCollectionListItemSuffix}`,
            disabled: collection.totalItemCount + itemsToAddCount > MAX_ITEMS_PER_COLLECTION,
          });
        }
        return filtered;
      }, []);
    }
    return items;
  });

  const getCollectionListItemContent = (collection: ICollectionInfo, isNewlyCreated: boolean, limitedUserVisibility: boolean, isAdmin: boolean): JSX.Element => {
    const showOwnerName = FeatureFlags.collectionsGAEnabled && !limitedUserVisibility
      && isAdmin && !!collection.ownerName;

    return <div className={CollectionsDialogStyles.CollectionListItemContainer}>
      <div className={CollectionsDialogStyles.CollectionName} style={{ width: !showOwnerName && '100%' }}>
        <div className={CollectionsDialogStyles.CollectionListItemText}>{collection.name}</div>
        {isNewlyCreated && <NewBadge />}
      </div>
      {showOwnerName && <span data-tb-test-id={'owner-name'} className={CollectionsDialogStyles.OwnerName}>
        <span className={CollectionsDialogStyles.OwnerPrefix}>, {Messages.collectionOwnerPrefix()}</span>
        {collection.ownerName}
      </span>}
    </div>;
  };

  export class Contents extends React.PureComponent<ContentsProps, ContentsState> {
    private collectionsCache: SparseCachedList<ICollectionInfo, ICollectionInfo>;
    private listRef: React.RefObject<List> = React.createRef();
    private isUnmounted = false;

    public constructor(props: ContentsProps) {
      super(props);

      this.state = {
        busy: false,
        error: undefined,
        allCollections: undefined, // undefined means not-yet-fetched
        searchResults: undefined,
        collectionsToUpdate: new Set<string>(),
        searchTerm: undefined,
        useLocalFiltering: false,
      };
    }

    public componentDidMount() {
      this.updateCollectionsCacheWithNewQuery().then(() => {
        this.updateState({ useLocalFiltering: this.state.allCollections?.length < CollectionsBlockSize });
      });
    }

    public componentDidUpdate(prevProps: ContentsProps, prevState: ContentsState) {
      if (prevProps.isAdmin !== this.props.isAdmin) {
        this.updateCollectionsCacheWithNewQuery();
      }

      if (prevState.searchTerm !== this.state.searchTerm) {
        this.props.manageItemsTelemetry.isSearchingOnClose = !!this.state.searchTerm;
      }

      if (!prevState.allCollections && this.state.allCollections) {
        // focus first collection after collections have been fetched
        const listDom = ReactDOM.findDOMNode(this.listRef.current) as HTMLDivElement;
        const firstListElement = listDom?.querySelector<HTMLElement>(`[role="option"]`);
        firstListElement?.focus();
      }
    }

    public componentWillUnmount() {
      this.isUnmounted = true;
    }

    private updateState<K extends keyof ContentsState>(newState: Pick<ContentsState, K>): void {
      if (!this.isUnmounted) {
        this.setState(newState);
      }
    }

    private async updateCollectionsCacheWithNewQuery(searchTerm?: string) {
      try{
        // clear existing collections
        !this.state.useLocalFiltering && this.updateState({
          searchResults: undefined,
          allCollections: undefined,
        });

        const ownerFilter = !this.props.isAdmin ? [ ServerFilterClauses.serverFilterClauses.owner(this.props.userId) ] : [];
        const searchFilter = searchTerm ? [ServerFilterClauses.serverFilterClauses.search(searchTerm)] : [];
        const query = new CollectionsResourceQuery()
          .withOrder([Order.desc(OrderKeys.updatedTime)])
          .withFilters(ownerFilter.concat(searchFilter));
        this.collectionsCache = new SparseCachedList<ICollectionInfo, ICollectionInfo>(query, _.identity, CollectionsBlockSize);

        // fetch the first block of the cache after initialization
        await this.fetchCollections(0, CollectionsBlockSize, searchTerm);
      } catch (e) {
        this.updateState({ error: Messages.collectionsFailedToLoad() });
      }
    }

    private handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
      if(this.state.useLocalFiltering) {
        return;
      }

      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const startIndex = Math.floor(scrollTop / CollectionsDialogStyles.COLLECTION_ITEM_SCROLL_HEIGHT);
      const itemsToFetch = Math.ceil(clientHeight / CollectionsDialogStyles.COLLECTION_ITEM_SCROLL_HEIGHT);
      const endIndex = startIndex + itemsToFetch;
      await this.fetchCollections(startIndex, endIndex);
    }

    public render(): JSX.Element {
      /* eslint-disable jsx-a11y/alt-text */
      return (
        <>
          <div className={CollectionsDialogStyles.ItemsToAdd}>{this.getItemsToAddMessage()}</div>
          {this.getCollectionsListElement()}

          <div className={!this.state.error ? CollectionsDialogStyles.ActionButtonsFlexContainer : ''}>
            {!this.state.error && <ButtonWidget
                title={Messages.newCollection()}
                buttonType={ButtonType.LowEmphasis}
                testId={ManageItemsInCollectionsCreateButtonTestId}
                handleClick={this.handleCreateCollectionClicked}
                className={CollectionsDialogStyles.CreateCollectionButton}
              >
                <img src={imgPath('plus_14px-small.svg')} role={'presentation'}/><span>{' ' + Messages.newCollection()}</span>
              </ButtonWidget>
            }
            <div className={CollectionsDialogStyles.CreateCancelButtons}>
              <ButtonWithSpinner
                title={Messages.cancel()}
                buttonType={ButtonType.Outline}
                clicked={this.props.closeDialog}
                testId={ManageItemsInCollectionsCancelButtonTestId}
                disabled={this.state.busy}
              />
              <span className="tb-margin-left-wide">
                <ButtonWithSpinner
                  title={Messages.add()}
                  buttonType={ButtonType.Primary}
                  clicked={this.saveChanges}
                  testId={ManageItemsInCollectionsSaveButtonTestId}
                  disabled={this.state.busy || !!this.state.error || this.state.collectionsToUpdate.size === 0}
                  showSpinner={this.state.busy}
                />
              </span>
            </div>
          </div>
        </>
      );
    }

    private getItemsToAddMessage(): string {
      if (this.props.items.length > 1) {
        return Messages.addMultipleItemsToCollection({ ITEM_COUNT: String(this.props.items.length) });
      } else if (this.props.items.length === 1) {
        return Messages.addSingleItemToCollection({ ITEM_NAME: this.props.items[0].name });
      }
      return undefined;
    }

    private async fetchCollections(startIndex: number, endIndex: number, searchTerm?: string): Promise<void> {
      try {
        const collections = await this.collectionsCache.getItems(startIndex, endIndex - 1);
        const targetCollectionLuids = this.props.targetCollectionLuids ?? [];

        if(FeatureFlags.collectionsGAEnabled && !this.isUnmounted
          && targetCollectionLuids.length > 0 && collections.length > 0 && !searchTerm) {
          // TFS 1226348: make sure newly created collections are in get response when going to CRASS
          const collectionLuids = collections.map((c) => c.luid);
          const containsTargetCollectionLuids = _.intersection(targetCollectionLuids, collectionLuids).length === targetCollectionLuids.length;
          if(!containsTargetCollectionLuids) {
            // if our get response doesn't have all collections, fetch again
            this.updateCollectionsCacheWithNewQuery();
            return;
          }
        }

        // If the given collection is specified as a "target collection" (e.g., coming from the
        // 'Create Collection' dialog), start it off as checked and add to `collectionsToUpdate`.
        targetCollectionLuids.forEach((collectionLuid) => {
          this.updateItemIsInCollectionMap(collectionLuid, true);
        });

        if(searchTerm) {
          this.updateState({ searchResults: collections });

          /** TFS 1239554: If the user immediately searches when the dialog is opened, before it has been populated
           * with all collections, we may hit a race condition where we've specified local filtering but have already
           * sent a search term request to CS. If we hit this condition, redirect to local search.
           */
          if(this.state.useLocalFiltering) {
            this.localSearch(searchTerm);
            return;
          }
        } else {
          this.updateState({ allCollections: collections });
        }
      } catch (e) {
        this.updateState({ error: Messages.collectionsFailedToLoad() });
      }
    }

    private getCollectionsListElement(): JSX.Element | JSX.Element[] {
      if (this.state.error) {
        return (
          <CompactInlineAlertWidget
            testId={ManageItemsInCollectionsErrorTestId}
            type={InlineAlertType.Error}
          >
            {this.state.error}
          </CompactInlineAlertWidget>
        );
      } else if (!FeatureFlags.collectionsGAEnabled && !this.state.allCollections) {
        return (
          <div className={CollectionsDialogStyles.Spinner} >
            <ActivitySpinnerWidget
              spinnerSize={ActivitySpinnerSize.Small}
              testId="collections-list-spinner" />
          </div>
        );
      }

      let collectionsToDisplay = this.state.searchResults ?? this.state.allCollections;
      this.props.manageItemsTelemetry.numCollectionsShown = collectionsToDisplay ? collectionsToDisplay.length : 0;
      const items = getCollectionsListItems(collectionsToDisplay, this.isNewlyCreated, this.props.limitedUserVisibility, this.props.isAdmin, this.props.items.length);

      if(FeatureFlags.collectionsGAEnabled) {
        /* eslint-disable jsx-a11y/no-autofocus */
        return <FilteringList
          items={items}
          selectedValues={this.state.collectionsToUpdate}
          className={CollectionsDialogStyles.CollectionsFilteringList}
          onSelect={this.updateItemIsInCollectionMap}
          onScroll={this.handleScroll}
          testId={ManageItemsInCollectionsListContainerTestId}
          onFilterChange={this.searchCollections}
          searchPlaceholder={Messages.addToCollections_searchPlaceHolder()}
          searchText={this.state.searchTerm}
          autoFocus
        />;
      } else {
        return <List
          items={items}
          selectedValues={this.state.collectionsToUpdate}
          className={CollectionsDialogStyles.CollectionsList}
          onSelect={this.updateItemIsInCollectionMap}
          onScroll={this.handleScroll}
          ref={this.listRef}
          testId={ManageItemsInCollectionsListContainerTestId}
        />;
      }
    }

    private isNewlyCreated = (collection: ICollectionInfo) => !!this.props.targetCollectionLuids?.includes(collection.luid);

    /**
     * Search filter. If all collections are fetched in one page, do a local search
     * with a simple substring match that mimicks the behavior of the LocallyFilteringList widget
     * but filters on collection name OR owner name. If we don't have all collections,
     * send search requests to Collections Service as a text filter.
     * 
     * Note: This does result in two different search experiences, substring match vs.
     * Elastic Search, which will yield different results. Design is prioritizing
     * a quick search with no server round trips for majority of users and will live with 
     * this discrepancy.
     */
    private searchCollections = (searchTerm: string): void => {
      // set a limit to avoid request too long
      if(searchTerm.length > 100) {
        const truncatedSearchTerm = searchTerm.substr(0, 100);
        if(truncatedSearchTerm !== this.state.searchTerm) {
          this.updateState({ searchTerm: truncatedSearchTerm });
        }
        return;
      }
      this.updateState({ searchTerm });

      if(this.state.useLocalFiltering) {
        this.localSearch(searchTerm);
      } else {
        this.debouncedCSSearch(searchTerm);
      }
    }

    private localSearch = (searchTerm: string): void => {
      const localSearchResults: ICollectionInfo[] = searchTerm ?
        this.state.allCollections?.filter(item => this.caseInsensitiveNameOrOwnerMatch(item, searchTerm)) :
        undefined;
      this.updateState({ searchResults: localSearchResults });
    }

    private csSearch = (searchTerm: string): void => {
      // TFS 1239554 -- redirect to local search if useLocalFiltering true
      if(this.state.useLocalFiltering) {
        this.localSearch(searchTerm);
        return;
      }

      this.updateCollectionsCacheWithNewQuery(searchTerm);
    }
    private debouncedCSSearch = _.debounce(this.csSearch, SEARCH_FILTER_TYPING_TIMEOUT);

    private caseInsensitiveNameOrOwnerMatch = (item: ICollectionInfo, filterValue: string) =>
      !!item && (item.name!.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0 ||
      item.ownerName?.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0);

    private saveChanges = async () => {
      const promises: Promise<AddRemoveCollectionItemsData>[] = [];
      this.state.collectionsToUpdate.forEach(luid => {
        promises.push(addItemsToCollection(luid, this.props.items));
      });

      this.updateState({ busy: true });
      if (this.isUnmounted) {
        return;
      }

      try {
        // `Promise.all` rejects as soon as one of the promises rejects. To process all add item results even if one rejects,
        // map rejecting promises to their errors. Then, filter responses into add items responses and thrown errors.
        const results = await Promise.all(promises.map(p => p.catch(e => e)));
        const addItemsResponses: AddRemoveCollectionItemsData[] = results.filter((result) => isAddItemsResponse(result));
        const errored = results.filter((result) => !isAddItemsResponse(result));

        this.handleAddItemsResponses(addItemsResponses, errored);
      } catch {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.unexpectedError()
        });
      } finally {
        this.updateState({ busy: false });
        await delayPromise(100); // Delay a short amount so that any action toast will appear before the dialog closes. It looks better this way.

        const collectionLuids = this.state.allCollections.map((c) => c.luid);
        const containsNewCollections = _.intersection(this.props.targetCollectionLuids || [], collectionLuids).length > 0;
        this.props.manageItemsTelemetry.itemsAddedToNewCollections = containsNewCollections;
        this.props.manageItemsTelemetry.numCollectionsAddedTo = this.state.collectionsToUpdate.size;
        this.props.manageItemsTelemetry.result = 'ok';
        sendManageItemsEvent(this.props.manageItemsTelemetry, this.props.spaceInfo);

        this.props.closeDialog();
      }
    }

    private updateItemIsInCollectionMap = (luid: string, checked?: boolean) => {
      if(_.isUndefined(luid) || _.isEmpty(luid)) {
        return;
      }

      const included = this.state.collectionsToUpdate.has(luid);
      const collectionsToUpdate = new Set(this.state.collectionsToUpdate);
      if(!included || checked){
        collectionsToUpdate.add(luid);
      } else {
        collectionsToUpdate.delete(luid);
      }

      this.updateState({ collectionsToUpdate });
    }

    private handleAddItemsResponses = (addItemsResponses: AddRemoveCollectionItemsData[], errored: any[]) => {
      // There are two types of errors. `errored` contains collection-level errors (e.g. user unauthorized to add to collection).
      // `addItemsResponses` could still contain item-level errors (e.g. unable to add 1 item out of 3 to collection), so go
      // through `addItemsResponses` to find successful add results.
      const successfulResults: AddRemoveCollectionItemsData[] = [];
      addItemsResponses.forEach((result) => {
        // if all items are added or already in the collection, count as a successful result
        if(result.numItemsSuccessfullyUpdated === this.props.items.length) {
          successfulResults.push(result);
        } else {
          // swallow DuplicateKey errors
          const duplicateKeys = result.errors.filter((error) => error.errorCode === CollectionsErrorCode.DUPLICATE_KEY);
          if(duplicateKeys.length === this.props.items.length - result.numItemsSuccessfullyUpdated) {
            successfulResults.push(result);
          }
        }
      });

      const countCollectionsSuccessful = successfulResults.length;
      const countCollectionsFailed = addItemsResponses.length - countCollectionsSuccessful;

      if(countCollectionsSuccessful > 0) {
        showActionableToast({
          testId: ManageItemsInCollectionsSuccessToastTestId,
          message: this.getSuccessMessage(countCollectionsSuccessful),
          actionMessage: countCollectionsSuccessful === 1 ? Messages.goToCollection() : Messages.goToCollections(),
          action: () => { countCollectionsSuccessful === 1
            ? Location.instance.setUrl(this.props.siteUrls.collectionUrl(successfulResults[0].collectionLuid))
            : Location.instance.setUrl(this.props.siteUrls.collectionsUrl());
          }
        }, ToastType.Success);
      }

      if(countCollectionsFailed > 0 || errored.length > 0){
        const count = countCollectionsFailed + errored.length;
        const toaster = ToasterService.instance;
        toaster.pop({
          type: ToastType.Error,
          text: Messages.collectionsAddItemsToCollectionsError({
            COLLECTION_COUNT: count,
            COLLECTION_COUNT_LOC: Formatters.formatCount(count)
          }),
          testId: ManageItemsInCollectionsErrorToastTestId
        });
      }
    }

    private getSuccessMessage = (successCount: number): string => {
      let successMessage;
      if(this.props.items.length === 1) {
        // if only attempting to add 1 item, can supply content name in message
        const name = this.props.items[0].name;
        successMessage = successCount === 1
          ? Messages.collectionsAddItemToOneCollectionSuccess({ CONTENT_NAME: L10n.elideIfLong(name) })
          : Messages.collectionsAddItemToMultCollectionsSuccess({
            CONTENT_NAME: L10n.elideIfLong(name),
            COLLECTIONS_COUNT: L10n.localizedCount(successCount)
          });
      } else {
        successMessage = successCount === 1
          ? Messages.collectionsAddItemsToOneCollectionSuccess({CONTENT_COUNT: L10n.localizedCount(this.props.items.length)})
          : Messages.collectionsAddItemsToMultCollectionsSuccess({
            CONTENT_COUNT: L10n.localizedCount(this.props.items.length),
            COLLECTIONS_COUNT: L10n.localizedCount(successCount)
          });
      }
      return successMessage;
    }

    private handleCreateCollectionClicked = () => {
      this.props.manageItemsTelemetry.numCollectionsCreatedAttempts = this.props.manageItemsTelemetry.numCollectionsCreatedAttempts + 1;
      this.props.manageItemsTelemetry.creatingCollection = true;
      this.props.manageItemsTelemetry.isSearchingOnClose = false;
      this.props.createEmptyCollection(this.props.items, this.props.manageItemsTelemetry);
      this.props.closeDialog();
    };
  }

  const isAddItemsResponse = (result: any) => {
    return result.hasOwnProperty('numItemsSuccessfullyUpdated');
  };

  export const mapContentsStateProps: MapStateToProps<ContentsStateProps> =
  (state: AppState) => ({
    limitedUserVisibility: CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(state)
  });

  const mapContentsDispatchProps: MapDispatchToProps<ContentsDispatchProps> = dispatch => bindActionCreators({
    createEmptyCollection: createEmptyCollectionAction,
  }, dispatch);

  export const ConnectedContents = connect(mapContentsStateProps, mapContentsDispatchProps)(Contents);

  async function addItemsToCollection(luid: string, items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    const query = new CollectionItemsResourceQuery(luid);
    return query.addItemsToCollection(items);
  }

  export function open(opts: Options): Promise<any> {
    const closeDialog = () => {
      if (opts.manageItemsTelemetry.result !== 'ok' && !opts.manageItemsTelemetry.creatingCollection) {
        opts.manageItemsTelemetry.result = 'cancel';
        sendManageItemsEvent(opts.manageItemsTelemetry, opts.spaceInfo);
      }
      modalInstance && modalInstance.close();
    };
    opts.manageItemsTelemetry.creatingCollection = false;

    const modalInstance = ReactModalService.open({
      title: Messages.addToCollectionsDialogTitle(),
      testId: ManageItemsInCollectionsDialogTestId,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      dismissDialogOnCloseAction: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: CollectionsDialogStyles.ADD_TO_DIALOG_WIDTH + 'px' },
      dialogContent: <ConnectedContents {...opts} closeDialog={closeDialog} />,
    });
    return modalInstance.result;
  }
}

export function NewBadge(): JSX.Element {
  return <span className={CollectionsDialogStyles.NewBadge}>
    <span style={{marginLeft: 8, marginRight: 8}}>{Messages.newBadge()}</span>
  </span>;
}
